import React, { useState, useEffect, useCallback, useRef } from "react";
import { FiUser } from "react-icons/fi";
import ToggleButton from './shared/ToggleButton';
import { BUTTON_STYLES } from './shared/buttonStyles';
import { typography } from '../styles/typography';
import { useNavigate } from "react-router-dom";
import axios, { API_BASE_URL } from '../config/api';
import { useAuth, useUser, useClerk } from '@clerk/clerk-react';
import { useResponsive } from "../hooks/useResponsive";
import { useClickOutside } from "../hooks/useClickOutside";

interface SessionItem {
  id: string;
  name: string;
  created_at: string;
  has_analysis: boolean;
  latest_job_id: string | null;
  latest_job_status: string | null;
}

interface SidebarProps {
  onSelectSession: (session: SessionItem | null) => void;
  selectedSessionId?: string;
  onSidebarToggle?: (collapsed: boolean) => void;
  refreshTrigger?: number;
  UploadButton?: React.ReactElement;
  onOptimisticUpdate?: (callback: (sessionId: string) => void) => void;
  forceCollapse?: boolean;
  optimisticSessions?: SessionItem[];
  onSessionsConfirmed?: (confirmedIds: string[]) => void;
}

const Sidebar: React.FC<SidebarProps> = ({
  onSelectSession,
  selectedSessionId,
  onSidebarToggle,
  refreshTrigger,
  UploadButton,
  onOptimisticUpdate,
  forceCollapse,
  optimisticSessions,
  onSessionsConfirmed
}) => {
  const [sessions, setSessions] = useState<SessionItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'connected' | 'disconnected' | 'slow'>('connected');
  const [retryCount, setRetryCount] = useState(0);
  const [optimisticProcessingIds, setOptimisticProcessingIds] = useState<Set<string>>(new Set());
  const { isSignedIn, getToken, isLoaded } = useAuth();
  const { user } = useUser();
  const { signOut } = useClerk();
  const navigate = useNavigate();
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  const userMenuRef = useClickOutside<HTMLDivElement>(
    () => setShowUserDropdown(false),
    { enabled: showUserDropdown }
  );

  // Refs let fetchSessions read the latest values without invalidating its useCallback identity
  const optimisticSessionsRef = useRef(optimisticSessions);
  const onSessionsConfirmedRef = useRef(onSessionsConfirmed);
  useEffect(() => { optimisticSessionsRef.current = optimisticSessions; }, [optimisticSessions]);
  useEffect(() => { onSessionsConfirmedRef.current = onSessionsConfirmed; }, [onSessionsConfirmed]);

  const handleSidebarScroll = useCallback(() => {
    if (scrollContainerRef.current) {
      sessionStorage.setItem('sidebar-scroll', String(scrollContainerRef.current.scrollTop));
    }
  }, []);

  useEffect(() => {
    const saved = sessionStorage.getItem('sidebar-scroll');
    if (saved && scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = Number(saved);
    }
  }, [sessions]);

  // Use responsive hook for mobile detection
  const { isMobile } = useResponsive({
    onMobileChange: (mobile, wasMobile) => {
      // Only auto-collapse if transitioning from desktop to mobile
      if (mobile && !wasMobile && !isCollapsed) {
        setIsCollapsed(true);
      }
    }
  });

  // Use click outside hook for mobile sidebar closing
  const sidebarRef = useClickOutside<HTMLElement>(
    () => setIsCollapsed(true),
    {
      enabled: isMobile && !isCollapsed,
      delay: 100,
      excludeSelectors: ['.sidebar-toggle-btn']
    }
  );

  // Handle force collapse prop
  useEffect(() => {
    if (forceCollapse && !isCollapsed) {
      setIsCollapsed(true);
      onSidebarToggle?.(true);
    }
  }, [forceCollapse, isCollapsed, onSidebarToggle]);

  // Stable token fetching function to avoid infinite loops
  const getAuthToken = useCallback(async () => {
    try {
      return await getToken();
    } catch (error) {
      console.error('Failed to get access token:', error);
      throw error;
    }
  }, [getToken]);

  // Optimistic update function to immediately mark session as processing
  const addOptimisticProcessing = useCallback((sessionId: string) => {
    setOptimisticProcessingIds(prev => new Set(Array.from(prev).concat(sessionId)));

    // Clear optimistic state after 10 seconds (polling should have updated by then)
    setTimeout(() => {
      setOptimisticProcessingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(sessionId);
        return newSet;
      });
    }, 10000);
  }, []);

  // Register optimistic update callback with parent
  useEffect(() => {
    if (onOptimisticUpdate) {
      onOptimisticUpdate(addOptimisticProcessing);
    }
  }, [onOptimisticUpdate, addOptimisticProcessing]);

  // Fetch sessions from the backend with enhanced resilience
  const fetchSessions = useCallback(async (isRetry = false, skipLoading = false) => {
    if (!isSignedIn) {
      setSessions([]);
      setIsLoading(false);
      setConnectionStatus('connected');
      return;
    }

    // Cancel any previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    try {
      // Only show loading spinner on initial load or after connection issues
      if (!skipLoading && (sessions.length === 0 || connectionStatus === 'disconnected')) {
        setIsLoading(true);
      }

      const token = await getAuthToken();
      const timeout = Math.min(5000 + (retryCount * 2000), 15000);

      const response = await axios.get<SessionItem[]>("/api/sessions", {
        timeout,
        signal: abortControllerRef.current.signal,
        headers: { Authorization: `Bearer ${token}` }
      });

      const sessionsData = Array.isArray(response.data) ? response.data : [];
      setSessions(sessionsData);
      setIsLoading(false);
      setConnectionStatus('connected');
      setRetryCount(0);

      // Notify parent of optimistic sessions now confirmed in the canonical list
      const optimisticList = optimisticSessionsRef.current;
      const notify = onSessionsConfirmedRef.current;
      if (optimisticList && optimisticList.length > 0 && notify) {
        const fetchedIds = new Set(sessionsData.map(s => s.id));
        const confirmed = optimisticList
          .filter(s => fetchedIds.has(s.id))
          .map(s => s.id);
        if (confirmed.length > 0) notify(confirmed);
      }

      // Clear optimistic processing for sessions that now have real status
      setOptimisticProcessingIds(prev => {
        const newSet = new Set(prev);
        sessionsData.forEach(session => {
          if (session.latest_job_status === 'processing' || session.latest_job_status === 'completed' || session.latest_job_status === 'failed') {
            newSet.delete(session.id);
          }
        });
        return newSet;
      });

      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }

    } catch (error) {
      if (axios.isCancel(error)) return;

      console.error(`Failed to fetch sessions (attempt ${retryCount + 1}):`, error);

      if (axios.isAxiosError(error)) {
        if (error.response?.status === 401) {
          setSessions([]);
          setConnectionStatus('disconnected');
          setIsLoading(false);
          return;
        }
        if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
          setConnectionStatus('slow');
        } else if (error.response?.status && error.response.status >= 500) {
          setConnectionStatus('slow');
        } else {
          setConnectionStatus('disconnected');
        }
      } else {
        setConnectionStatus('disconnected');
      }

      if (sessions.length === 0) setIsLoading(false);

      if (retryCount < 3 && !isRetry) {
        const backoffDelay = Math.min(1000 * Math.pow(2, retryCount), 8000);
        setRetryCount(prev => prev + 1);
        retryTimeoutRef.current = setTimeout(() => {
          fetchSessions(true);
        }, backoffDelay);
      }
    }
  }, [isSignedIn, sessions.length, retryCount, connectionStatus, getAuthToken]);

  useEffect(() => {
    fetchSessions();

    const getPollingInterval = () => {
      switch (connectionStatus) {
        case 'connected': return 5000;
        case 'slow': return 10000;
        case 'disconnected': return 15000;
        default: return 5000;
      }
    };

    const intervalId = setInterval(() => {
      fetchSessions();
    }, getPollingInterval());

    return () => {
      clearInterval(intervalId);
      if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
  }, [fetchSessions, connectionStatus]);

  // Trigger immediate refresh when refreshTrigger changes
  useEffect(() => {
    if (refreshTrigger !== undefined && refreshTrigger > 0) {
      setRetryCount(0);
      fetchSessions(false, true);
    }
  }, [refreshTrigger, fetchSessions]);




  const handleLogout = useCallback(() => {
    signOut({ redirectUrl: '/' });
    setShowUserDropdown(false);
  }, [signOut]);

  const toggleUserDropdown = useCallback(() => {
    setShowUserDropdown(prev => !prev);
  }, []);

  const toggleSidebar = useCallback(() => {
    const newCollapsedState = !isCollapsed;
    setIsCollapsed(newCollapsedState);
    onSidebarToggle?.(newCollapsedState);
  }, [isCollapsed, onSidebarToggle]);

  const handleButtonHover = {
    enter: (e: React.MouseEvent<HTMLButtonElement>) => {
      e.currentTarget.style.background = BUTTON_STYLES.BACKGROUND_HOVER;
      e.currentTarget.style.color = BUTTON_STYLES.TEXT_HOVER;
    },
    leave: (e: React.MouseEvent<HTMLButtonElement>) => {
      e.currentTarget.style.background = BUTTON_STYLES.BACKGROUND_DEFAULT;
      e.currentTarget.style.color = BUTTON_STYLES.TEXT_DEFAULT;
    }
  };

  const handleSessionSelect = useCallback((session: SessionItem) => {
    onSelectSession(session);
  }, [onSelectSession]);

  // Merge optimistic sessions with canonical list, prepended and deduped by id
  const displayedSessions = React.useMemo(() => {
    if (!optimisticSessions || optimisticSessions.length === 0) return sessions;
    const existingIds = new Set(sessions.map(s => s.id));
    const placeholders = optimisticSessions.filter(s => !existingIds.has(s.id));
    return placeholders.length > 0 ? [...placeholders, ...sessions] : sessions;
  }, [sessions, optimisticSessions]);

  const truncateText = (text: string, maxLength: number = 25): string => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString();
  };

  const getSessionStatusDot = (session: SessionItem): React.ReactNode => {
    const isOptimisticallyProcessing = optimisticProcessingIds.has(session.id);

    let color: string | null = null;
    let pulse = false;

    if (isOptimisticallyProcessing || session.latest_job_status === 'pending' || session.latest_job_status === 'processing') {
      color = '#eab308'; // yellow
      pulse = true;
    } else if (session.latest_job_status === 'failed') {
      color = '#ef4444'; // red
    } else if (session.latest_job_status === 'completed') {
      color = '#10b981'; // green
    }

    if (!color) return null;

    return (
      <span style={{
        display: 'inline-block',
        width: '6px',
        height: '6px',
        borderRadius: '50%',
        backgroundColor: color,
        marginLeft: '6px',
        flexShrink: 0,
        animation: pulse ? 'pulse-dot 1.5s ease-in-out infinite' : undefined,
      }} />
    );
  };

  return (
    <>
      {/* Mobile overlay */}
      {isMobile && !isCollapsed && (
        <div 
          className="sidebar-overlay" 
          onClick={() => setIsCollapsed(true)}
          aria-hidden="true"
        />
      )}
      
      {/* Sidebar toggle button for collapsed state */}
      {isCollapsed && (
        <ToggleButton
          onClick={toggleSidebar}
          ariaLabel="Open sidebar"
          ariaExpanded={false}
          onMouseEnter={handleButtonHover.enter}
          onMouseLeave={handleButtonHover.leave}
          style={{
            position: 'fixed',
            top: '1rem',
            left: '1rem',
            zIndex: 1001
          }}
        />
      )}
      
      <aside 
        ref={sidebarRef}
        className={`sidebar ${isCollapsed ? 'sidebar-collapsed' : ''} ${isMobile ? 'sidebar-mobile' : ''}`}
        role="navigation"
        aria-label="Main navigation"
      >
        {/* Logo Section - Fixed at top */}
        <div className="sidebar-header">
          <div className="sidebar-logo" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
            <img 
              src="/gp_commit_logo_1.png" 
              alt="TrackAI Logo" 
              className="sidebar-logo-img" 
            />
          </div>
          <ToggleButton
            onClick={toggleSidebar}
            ariaLabel={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            ariaExpanded={!isCollapsed}
            onMouseEnter={handleButtonHover.enter}
            onMouseLeave={handleButtonHover.leave}
          />
        </div>

        {/* Upload Button */}
        <div className="upload-button-container">
          {UploadButton || (
            <button 
              className="upload-button" 
              aria-label="Upload new session"
            >
              Upload Session
            </button>
          )}
        </div>
        
        {/* Scrollable Analysis List */}
        <div className="sidebar-content" ref={scrollContainerRef} onScroll={handleSidebarScroll}>
          <div className="sidebar-section">
            <div className="sidebar-section-header">
              <h3>Your Sessions</h3>
            </div>
            {isLoading ? (
              <div className="loading-indicator" role="status" aria-live="polite">
                Loading...
              </div>
            ) : displayedSessions.length > 0 ? (
              <ul className="analysis-list">
                {displayedSessions.map(session => (
                  <li
                    key={session.id}
                    className={`${selectedSessionId === session.id ? "selected" : ""} ${
                      session.latest_job_status === 'processing' ||
                      optimisticProcessingIds.has(session.id) ? "processing" : ""
                    }`}
                    onClick={() => handleSessionSelect(session)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleSessionSelect(session);
                      }
                    }}
                    tabIndex={0}
                    role="button"
                    aria-label={`Select session ${session.name}`}
                    aria-pressed={selectedSessionId === session.id}
                  >
                    <div className="analysis-info">
                      <div className="analysis-name" style={{
                        fontSize: typography.base,
                        fontWeight: '500',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}>
                        {truncateText(session.name)}
                      </div>
                      <div className="analysis-date" style={{ fontSize: typography.sm, display: 'flex', alignItems: 'center' }}>
                        {formatDate(session.created_at)}{getSessionStatusDot(session)}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="empty-state">
                No sessions yet. Upload your first session data!
              </div>
            )}
          </div>
        </div>

        {/* User Section - Fixed at bottom */}
        {isSignedIn && isLoaded && (
          <div className="sidebar-footer">
            <div className="sidebar-user-menu" ref={userMenuRef}>
              <div 
                className="user-info" 
                onClick={toggleUserDropdown}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    toggleUserDropdown();
                  }
                }}
                tabIndex={0}
                role="button"
                aria-label="User menu"
                aria-expanded={showUserDropdown}
                aria-haspopup="true"
              >
                {user?.imageUrl ? (
                  <img 
                    src={user.imageUrl} 
                    alt={`${user.fullName || user.firstName || 'User'} avatar`} 
                    className="user-avatar"
                    crossOrigin="anonymous"
                    referrerPolicy="no-referrer"
                    onError={(e) => {
                      // Fallback to placeholder if image fails to load
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      const placeholder = target.nextElementSibling as HTMLElement;
                      if (placeholder) {
                        placeholder.style.display = 'flex';
                      }
                    }}
                  />
                ) : null}
                <div 
                  className="user-avatar-placeholder"
                  style={{ display: user?.imageUrl ? 'none' : 'flex' }}
                >
                  <FiUser aria-hidden="true" />
                </div>
                <div className="user-details">
                  <span className="user-name">{user?.fullName || user?.firstName || user?.emailAddresses?.[0]?.emailAddress}</span>
                </div>
              </div>
              {showUserDropdown && (
                <div className="user-dropdown" role="menu">
                  <button
                    onClick={() => {
                      setShowUserDropdown(false);
                      navigate('/dashboard/settings');
                    }}
                    className="user-dropdown-item"
                    role="menuitem"
                  >
                    Settings
                  </button>
                  <a
                    href="/legal/terms/latest"
                    className="user-dropdown-item"
                    role="menuitem"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Terms & Policies
                  </a>
                  <a
                    href={`${API_BASE_URL}/docs`}
                    className="user-dropdown-item"
                    role="menuitem"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    API Docs
                  </a>
                  <button
                    onClick={handleLogout}
                    className="user-dropdown-item"
                    role="menuitem"
                  >
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </aside>
    </>
  );
};

export default Sidebar;